/**
 * Tap to call, tap to write.
 *
 * The number is held in E.164 because that is the only shape the phone system recognises, and it
 * is shown as (727) 337-7775 because that is the only shape a person reads back out loud. The
 * `tel:` href takes the stored form; the label takes the readable one.
 *
 * A missing number is called out rather than left blank. A household with nobody to ring is a
 * household that cannot be told a visit moved, which is the promise this system exists to keep.
 *
 * A NUMBER MARKED "DO NOT CALL" IS WRITTEN OUT, NEVER OFFERED (ops #44 step two): the do-not-contact
 * mark that holds calls, passed as `marked`, draws the number as text with "Do not call" in front of
 * it and no `tel:` link. That is UX and not the control, and it is all ops can do for voice: a call
 * is placed from a handset outside the software.
 */
import type { HoldingMark } from '../api.ts';
import { formatPhone } from '../format.ts';

export function CallLink({
  phoneE164,
  marked = null,
}: {
  phoneE164: string | null;
  /** The mark that holds calls to her, on her, this number or her address, or null for none. */
  marked?: HoldingMark | null;
}) {
  if (!phoneE164) {
    return (
      <p className="text-sm text-coral">
        No phone number. Nobody here can be told when a visit moves.
      </p>
    );
  }
  if (marked) {
    return (
      <p className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-gold/60 bg-parchment px-4 text-base">
        Do not call {formatPhone(phoneE164)}
      </p>
    );
  }
  return (
    <a
      href={`tel:${phoneE164}`}
      className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-base font-medium"
    >
      Call {formatPhone(phoneE164)}
    </a>
  );
}

export function MailLink({ email }: { email: string | null }) {
  if (!email) return null;
  return (
    <a
      href={`mailto:${email}`}
      className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm break-all"
    >
      {email}
    </a>
  );
}
